import { createTheme, responsiveFontSizes, useMediaQuery } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BLUE,
  TEAL,
  GREEN,
  PURPLE,
  GRAY,
  ORANGE,
} from '@social-garden/utils/colors.ts';
import { ThemeOptions } from '@mui/material/styles';
import { assertUnreachable } from '@social-garden/utils/helpers.ts';
import {
  enUS as datePickerEnUS,
  deDE as datePickerDeDE,
} from '@mui/x-date-pickers/locales';
import {
  enUS as dataGridEnUS,
  deDE as dataGridDeDE,
  arSD as dataGridArSD,
} from '@mui/x-data-grid/locales';
import {
  enUS as coreEnUS,
  deDE as coreDeDE,
  arSD as coreArSD,
} from '@mui/material/locale';
import {
  darkThemeOptions,
  lightThemeOptions,
} from '@/constants/ThemeOptions.ts';
import useColorMode from './useColorMode.ts';

const MUI_RESOURCES: Record<string, object[]> = {
  en: [datePickerEnUS, dataGridEnUS, coreEnUS],
  de: [datePickerDeDE, dataGridDeDE, coreDeDE],
  ar: [datePickerEnUS, dataGridArSD, coreArSD],
};

export default function useCreateTheme() {
  const {
    i18n: { resolvedLanguage, languages, dir },
  } = useTranslation(['manager']);
  const { mode } = useColorMode();
  const systemPrefersDarkColor = useMediaQuery('(prefers-color-scheme: dark)');

  const theme = useMemo(() => {
    let colorThemeOptions: ThemeOptions;

    switch (mode) {
      case 'light':
        colorThemeOptions = lightThemeOptions;
        break;
      case 'dark':
        colorThemeOptions = darkThemeOptions;
        break;
      case 'system':
        colorThemeOptions = systemPrefersDarkColor
          ? darkThemeOptions
          : lightThemeOptions;
        break;
      default:
        assertUnreachable(mode);
    }

    return createTheme(
      {
        ...colorThemeOptions,
        direction: dir(resolvedLanguage),
      },
      ...MUI_RESOURCES[resolvedLanguage ?? languages[0]],
    );
  }, [mode, resolvedLanguage, languages, systemPrefersDarkColor, dir]);

  return useMemo(
    () =>
      responsiveFontSizes(
        createTheme(theme, {
          palette: {
            applied: theme.palette.augmentColor({
              color: {
                ...BLUE,
                main: BLUE['600'],
              },
              name: 'applied',
            }),
            applicationDeclined: theme.palette.augmentColor({
              color: {
                ...GRAY,
                main: GRAY['600'],
              },
              name: 'applicationDeclined',
            }),
            previewPending: theme.palette.augmentColor({
              color: {
                ...TEAL,
                main: TEAL['600'],
              },
              name: 'previewPending',
            }),
            previewSubmitted: theme.palette.augmentColor({
              color: {
                ...PURPLE,
                main: PURPLE['600'],
              },
              name: 'previewSubmitted',
            }),
            contentPending: theme.palette.augmentColor({
              color: {
                ...TEAL,
                main: TEAL['700'],
              },
              name: 'contentPending',
            }),
            contentSubmitted: theme.palette.augmentColor({
              color: {
                ...GREEN,
                main: GREEN['600'],
              },
              name: 'contentSubmitted',
            }),
            reportFailed: theme.palette.augmentColor({
              color: {
                ...ORANGE,
                main: ORANGE['600'],
              },
              name: 'reportFailed',
            }),
            reportCompleted: theme.palette.augmentColor({
              color: {
                ...GREEN,
                main: GREEN['700'],
              },
              name: 'reportCompleted',
            }),
            discontinued: theme.palette.augmentColor({
              color: {
                ...GRAY,
                main: GRAY['700'],
              },
              name: 'discontinued',
            }),
            campaignSuspended: theme.palette.augmentColor({
              color: {
                ...GRAY,
                main: GRAY['700'],
              },
              name: 'campaignSuspended',
            }),
          },
        }),
      ),
    [theme],
  );
}
